import { platform } from '@fg/platform-sdk';
import { createLoadingScreen } from '@fg/ui-kit';
import '@fg/ui-kit/theme.css';
import Phaser from 'phaser';
import gameConfig from '../game.config';
import { BootScene } from './scenes/BootScene';
import { GameOverScene } from './scenes/GameOverScene';
import { GameScene } from './scenes/GameScene';
import { PreloadScene } from './scenes/PreloadScene';
import { TitleScene } from './scenes/TitleScene';

window.addEventListener('error', (e) => {
  platform.trackEvent('error', { message: e.message, source: e.filename, line: e.lineno });
});
window.addEventListener('unhandledrejection', (e) => {
  platform.trackEvent('error', { message: String(e.reason), kind: 'unhandled-rejection' });
});

async function boot() {
  await platform.init({ gameId: gameConfig.name, version: gameConfig.version });
  platform.loadingStart();

  const loading = createLoadingScreen({
    tip: 'Drag and drop fruits to merge them!',
    logo: buildWatermelonLogo(),
  });
  document.body.appendChild(loading.el);
  loading.show();

  // HiDPI rendering — canonical Phaser 3 "Supernapie" pattern (Phaser officially
  // promoted this in 2018; `resolution` config was removed in 3.16).
  // - Game internal size = window size * dpr → canvas backing-store is at
  //   physical pixels, so sprites/lines rasterize crisply on retina.
  // - `zoom: 1/dpr` is Phaser's CSS-display scale → canvas visually fills
  //   the viewport at CSS pixels.
  // - dpr capped at 2: dpr=3 displays would 9× the per-frame pixel fill,
  //   tanking frame rate on integrated GPUs. dpr=2 already gives crisp retina.
  //
  // Consequence: world coordinates are in PHYSICAL pixels, not CSS pixels.
  // CSS-pixel-tuned constants (radii, widths, gravity, velocities) are
  // multiplied by dpr at the entity/scene boundary — entities read `dpr`
  // from `registry`.
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: 'game',
    backgroundColor: '#ffe3b3',
    scale: {
      mode: Phaser.Scale.NONE,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      width: window.innerWidth * dpr,
      height: window.innerHeight * dpr,
      zoom: 1 / dpr,
    },
    render: { pixelArt: false, antialias: true, roundPixels: false },
    physics: {
      default: 'matter',
      // Gravity is world units / step² — scale by dpr so visual fall speed
      // matches the CSS-pixel-tuned design value (0.7).
      matter: { gravity: { x: 0, y: 0.7 * dpr }, enableSleeping: true },
    },
    scene: [BootScene, PreloadScene, TitleScene, GameScene, GameOverScene],
  });
  game.registry.set('dpr', dpr);

  // Keep canvas backing-store synced to viewport on window resize. rAF-debounced
  // so a burst of resize events causes one renderer realloc, not many.
  let resizeRaf = 0;
  const onWindowResize = () => {
    if (resizeRaf) return;
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = 0;
      game.scale.resize(window.innerWidth * dpr, window.innerHeight * dpr);
    });
  };
  window.addEventListener('resize', onWindowResize);
  game.events.once(Phaser.Core.Events.DESTROY, () => {
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    window.removeEventListener('resize', onWindowResize);
  });

  game.events.on('preload-progress', (frac: number) => loading.setProgress(frac));
  game.events.on('preload-complete', () => {
    loading.setProgress(1);
    setTimeout(() => loading.destroy(), 200);
    platform.loadingComplete();
  });
}

/**
 * Watermelon logo for the loading screen. The PNG is a 1536×1024 strip with 4
 * frames (384×1024 each). The fruit drawing sits in the vertical middle of
 * each frame, so we render the strip at frame-aspect (530×353) and crop to a
 * SQUARE viewport (140×140) by offsetting background-position vertically.
 * Result: only the round watermelon body shows, no tall empty bands above/below.
 */
function buildWatermelonLogo(): HTMLElement {
  const size = 140;
  const stripW = size * 4; // 4 frames laid out horizontally, frame 0 fills 1/4 of width
  const stripH = Math.round((size * 1024) / 384); // preserve frame's vertical aspect
  const yOffset = Math.round((stripH - size) / 2); // crop to the vertical middle
  const el = document.createElement('div');
  el.style.cssText = `
    width:${size}px; height:${size}px;
    background-image: url('${import.meta.env.BASE_URL}assets/sprites/fruit-watermelon.png');
    background-size: ${stripW}px ${stripH}px;
    background-position: 0 -${yOffset}px;
    background-repeat: no-repeat;
  `;
  return el;
}

boot().catch((err) => console.error('[fruit-merge-2d] boot failed', err));
